import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';
import { useToastStore } from '../../../stores/toast-store';

/**
 * Fatia 11.1 — useTakeOver canonical.
 *
 * Mutation hook para atendente retomar conversa em modo H_c (Cmd+R no
 * frontend ou clique no botão "Retomar" do AIConductingBanner). Transição
 * canonical H_c → H_a com audit backend `ai.conducting.resumed_by_human`.
 *
 * Em sucesso: invalida cache do detail da conversa para refletir mode='H_a'
 * (Composer reaparece + banner some).
 *
 * Capability gate backend: `core-conversation`. Erro 403 se modo atual !== H_c.
 */
export function useTakeOver(conversationId: string | undefined): {
  mutate: () => Promise<void>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      if (conversationId === undefined) {
        throw new Error('conversationId obrigatório para retomar conversa');
      }
      return api.takeOver(conversationId);
    },
    onSuccess: () => {
      if (conversationId === undefined) return;
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      useToastStore.getState().push({
        variant: 'success',
        title: 'Conversa retomada',
        description: 'Modo H_a (você dirige) restabelecido.',
        durationMs: 3500,
      });
    },
    onError: (err) => {
      useToastStore.getState().push({
        variant: 'error',
        title: 'Falha ao retomar conversa',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        durationMs: 6000,
      });
    },
  });

  return {
    mutate: async () => {
      await mutation.mutateAsync();
    },
    isPending: mutation.isPending,
    error: mutation.error,
  };
}
